<template>
  <div
    class="pdf-page-overlay"
    :data-page-number="pageNumber"
    @dragover.prevent="onDragOver"
    @drop.prevent="onDrop"
  >
    <v-stage ref="stage" :config="stageConfig" @click="handleStageClick">
      <v-layer ref="fieldLayer">
        <FieldRectangle
          v-for="field in fields"
          :key="field.id"
          :ref="el => setFieldRef(field.id, el)"
          :field="field"
          :scale="scale"
          :pageWidthPt="pageWidthPt"
          :pageHeightPt="pageHeightPt"
          :editable="editable"
          :selected="field.id === selectedFieldId"
          :status="fieldStatuses[field.id] || 'idle'"
          @select="onFieldSelect"
          @move="onFieldMove"
        />
        <v-transformer
          v-if="editable && hasSelectedField"
          ref="transformer"
          :config="transformerConfig"
        />
      </v-layer>
    </v-stage>
  </div>
</template>

<script>
import FieldRectangle from './FieldRectangle.vue';
import { FIELD_TYPES, ptToPixels, pixelsToPt, resolveAnnotationMinimums } from './fieldConstants.js';
import { useAnnotationFieldTypesStore } from '../../stores/annotationFieldTypes.js';

export default {
  name: 'PdfPageViewer',
  components: { FieldRectangle },
  props: {
    pageNumber: { type: Number, required: true },
    pageWidthPt: { type: Number, required: true },
    pageHeightPt: { type: Number, required: true },
    scale: { type: Number, default: 1 },
    fields: { type: Array, default: () => [] },
    selectedFieldId: { type: String, default: null },
    editable: { type: Boolean, default: false },
    fieldStatuses: { type: Object, default: () => ({}) },
  },
  emits: ['field-select', 'field-deselect', 'field-move', 'field-resize', 'field-drop'],
  data() {
    return {
      fieldRefs: {},
      _transformNode: null,
      // Pinia store for server-supplied per-type sizing rules (resize-handle
      // minimums). Populated in created(); FieldEditor calls ensure() once
      // for the whole editor session, so by the time the user can resize,
      // store.types is populated.
      _fieldTypesStore: null,
    };
  },
  created() {
    this._fieldTypesStore = useAnnotationFieldTypesStore();
  },
  computed: {
    displayWidth() {
      return Math.round(ptToPixels(this.pageWidthPt, this.scale));
    },
    displayHeight() {
      return Math.round(ptToPixels(this.pageHeightPt, this.scale));
    },
    stageConfig() {
      return {
        width: this.displayWidth,
        height: this.displayHeight,
      };
    },
    hasSelectedField() {
      return this.selectedFieldId != null &&
        this.fields.some(f => f.id === this.selectedFieldId);
    },
    selectedField() {
      if (!this.selectedFieldId) return null;
      return this.fields.find(f => f.id === this.selectedFieldId) || null;
    },
    transformerConfig() {
      const selectedType = this.selectedField?.type || 'Text';
      const { minWidthPt, minHeightPt } = resolveAnnotationMinimums(
        selectedType,
        this._fieldTypesStore?.types || null
      );
      const scale = this.scale;
      const stageW = this.displayWidth;
      const stageH = this.displayHeight;
      return {
        rotateEnabled: false,
        enabledAnchors: [
          'top-left', 'top-right', 'bottom-left', 'bottom-right',
          'middle-left', 'middle-right', 'top-center', 'bottom-center',
        ],
        borderStroke: '#6366F1',
        borderStrokeWidth: 1,
        anchorSize: 8,
        anchorStroke: '#6366F1',
        anchorFill: '#fff',
        anchorCornerRadius: 2,
        boundBoxFunc(oldBox, newBox) {
          const minW = ptToPixels(minWidthPt, scale);
          const minH = ptToPixels(minHeightPt, scale);

          // Enforce minimum dimensions
          if (Math.abs(newBox.width) < minW) {
            newBox.width = minW * (newBox.width < 0 ? -1 : 1);
            newBox.x = oldBox.x;
          }
          if (Math.abs(newBox.height) < minH) {
            newBox.height = minH * (newBox.height < 0 ? -1 : 1);
            newBox.y = oldBox.y;
          }

          // Boundary checks - prevent growing out of bounds but allow shrinking
          const isOutOfBounds =
            newBox.x < 0 ||
            newBox.y < 0 ||
            newBox.x + newBox.width > stageW ||
            newBox.y + newBox.height > stageH;

          if (isOutOfBounds) {
            const isShrinking =
              Math.abs(newBox.width) * Math.abs(newBox.height) <
              Math.abs(oldBox.width) * Math.abs(oldBox.height) * 0.95;

            if (isShrinking) {
              // Allow shrinking but clamp position to stay in bounds
              if (newBox.x < 0) newBox.x = 0;
              if (newBox.y < 0) newBox.y = 0;
              if (newBox.x + newBox.width > stageW)
                newBox.x = Math.max(0, stageW - newBox.width);
              if (newBox.y + newBox.height > stageH)
                newBox.y = Math.max(0, stageH - newBox.height);
              return newBox;
            }
            // Block growing out of bounds
            return oldBox;
          }

          return newBox;
        },
      };
    },
  },
  watch: {
    selectedFieldId() {
      this.$nextTick(() => this.attachTransformer());
    },
    hasSelectedField() {
      this.$nextTick(() => this.attachTransformer());
    },
  },
  methods: {
    setFieldRef(id, el) {
      if (el) this.fieldRefs[id] = el;
      else delete this.fieldRefs[id];
    },

    attachTransformer() {
      const tr = this.$refs.transformer?.getNode?.();
      if (!tr) return;

      // Detach previous transform listener
      if (this._transformNode) {
        this._transformNode.off('transformend', this.handleTransformEnd);
        this._transformNode = null;
      }

      if (!this.hasSelectedField) {
        tr.nodes([]);
        tr.getLayer()?.batchDraw();
        return;
      }

      const fieldComp = this.fieldRefs[this.selectedFieldId];
      const node = fieldComp?.getKonvaNode?.();
      if (node) {
        tr.nodes([node]);
        node.on('transformend', this.handleTransformEnd);
        this._transformNode = node;
        tr.getLayer()?.batchDraw();
      }
    },

    handleTransformEnd() {
      const node = this._transformNode;
      if (!node) return;

      const scaleX = node.scaleX();
      const scaleY = node.scaleY();

      // Reset scale to 1 and apply to size (same pattern as NotaryLaunchpad)
      node.scale({ x: 1, y: 1 });

      const selectedType = this.selectedField?.type || 'Text';
      const { minWidthPt, minHeightPt } = resolveAnnotationMinimums(
        selectedType,
        this._fieldTypesStore?.types || null
      );
      const minW = ptToPixels(minWidthPt, this.scale);
      const minH = ptToPixels(minHeightPt, this.scale);

      const newWidth = Math.max(minW, node.width() * scaleX);
      const newHeight = Math.max(minH, node.height() * scaleY);

      node.width(newWidth);
      node.height(newHeight);

      this.$emit('field-resize', {
        id: this.selectedFieldId,
        x: pixelsToPt(node.x(), this.scale),
        y: pixelsToPt(node.y(), this.scale),
        widthPt: pixelsToPt(newWidth, this.scale),
        heightPt: pixelsToPt(newHeight, this.scale),
      });

      // After Vue re-renders child shapes with new dimensions, refresh the transformer
      this.$nextTick(() => {
        this.$refs.transformer?.getNode?.()?.forceUpdate();
      });
    },

    handleStageClick(e) {
      // Click on empty stage area → deselect
      if (e.target === e.target.getStage()) {
        this.$emit('field-deselect');
      }
    },

    onFieldSelect(fieldId) {
      this.$emit('field-select', fieldId);
    },

    onFieldMove(data) {
      this.$emit('field-move', data);
    },

    // --- HTML5 Drag-and-Drop for new field placement ---
    onDragOver(e) {
      if (e.dataTransfer.types.includes('application/x-esign-field-type')) {
        e.dataTransfer.dropEffect = 'copy';
      }
    },

    /** Check whether there is a Konva field shape at the given client coordinates. */
    hasFieldAtPoint(clientX, clientY) {
      const stage = this.$refs.stage?.getNode?.();
      if (!stage) return false;
      const rect = stage.container().getBoundingClientRect();
      const shape = stage.getIntersection({ x: clientX - rect.left, y: clientY - rect.top });
      return shape != null && shape !== stage;
    },

    onDrop(e) {
      const fieldType = e.dataTransfer.getData('application/x-esign-field-type');
      if (!fieldType || !FIELD_TYPES[fieldType]) return;

      const rect = this.$el.getBoundingClientRect();
      const dropXPx = e.clientX - rect.left;
      const dropYPx = e.clientY - rect.top;

      // Emit the raw click point in PDF points. The parent (FieldEditor)
      // owns dimension resolution - it knows the page metadata and the
      // field-type rules - so it computes width/height from
      // resolveAnnotationDefaults and centers the field there. Keeping
      // PdfPageViewer ignorant of sizing rules keeps it reusable outside
      // the editor context.
      this.$emit('field-drop', {
        type: fieldType,
        pageNumber: this.pageNumber,
        dropXPt: pixelsToPt(dropXPx, this.scale),
        dropYPt: pixelsToPt(dropYPx, this.scale),
      });
    },
  },
  beforeUnmount() {
    if (this._transformNode) {
      this._transformNode.off('transformend', this.handleTransformEnd);
      this._transformNode = null;
    }
  },
};
</script>

<style scoped>
.pdf-page-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
}
</style>
